// Recipe Builder rb-10c-1b — the in-app variance alert (spec 0.24 §rb-10c-1; Thomas 2026-10-30): every recipes.manage
// holder sees a period's lines past their threshold until one of them acknowledges it (Q4: once for all, logged).
// FAIL-SAFE like SetupNudge: a load error renders nothing, so it can never block the app.
import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApi, useProfile } from '../contexts/ApiContext'
import { acknowledgeVarianceAlert, fetchVarianceAlerts, money, type VarianceAlert } from '../api/recipes'

export function VarianceAlertBanner() {
  const api = useApi()
  const profile = useProfile()
  const may = profile.role === 'OWNER' || (profile.permissions ?? []).includes('recipes.manage')
  const [alerts, setAlerts] = useState<VarianceAlert[]>([])
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try { setAlerts(await fetchVarianceAlerts(api)) } catch { setAlerts([]) }
  }, [api])
  useEffect(() => { if (may) void load() }, [may, load])

  if (!may || alerts.length === 0) return null
  const ack = async (id: string) => {
    setError(null)
    try { await acknowledgeVarianceAlert(api, id); setAlerts((a) => a.filter((x) => x.id !== id)) } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not acknowledge the alert')
    }
  }
  return (
    <div style={styles.bar} role="alert" aria-label="Variance alerts">
      {alerts.map((a) => (
        <div key={a.id} style={styles.line}>
          <span>
            <strong>{a.label}</strong> unexplained variance {a.pts.toFixed(2)} pts ({money(a.unexplained)}) in {a.period.name} —{' '}
            {a.direction === 'OVER' ? 'over' : 'under'} its {a.thresholdPts.toFixed(2)}-point threshold.
          </span>
          <span style={styles.actions}>
            <Link to={`/recipes/actual-vs-theoretical?periodId=${encodeURIComponent(a.period.id)}`} style={styles.link}>Open the report →</Link>
            <button type="button" style={styles.btn} onClick={() => void ack(a.id)} aria-label={`Acknowledge the ${a.label} alert for ${a.period.name}`}>Acknowledge</button>
          </span>
        </div>
      ))}
      {error && <div style={styles.error}>{error}</div>}
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  bar: { padding: '0.5rem 1rem', background: '#fef2f2', borderBottom: '1px solid #fecaca', fontFamily: 'system-ui, sans-serif', fontSize: 14, color: '#7f1d1d' },
  line: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '0.2rem 0' },
  actions: { display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 },
  link: { color: '#b91c1c', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' },
  btn: { border: '1px solid #fca5a5', background: '#fff', color: '#7f1d1d', borderRadius: 6, padding: '0.2rem 0.6rem', cursor: 'pointer' },
  error: { color: '#b91c1c', fontWeight: 600 },
}
